import { useEffect, useState } from 'react';
import { useShareLink } from '../../hooks';
import { CopyButton, ConfirmDialog } from '../common';

interface ShareLinkPanelProps {
  projectId: string;
}

export function ShareLinkPanel({ projectId }: ShareLinkPanelProps) {
  const { shareLink, loading, fetchShareLink, createShareLink, deleteShareLink } = useShareLink();
  const [isCreating, setIsCreating] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  useEffect(() => {
    fetchShareLink(projectId);
  }, [projectId, fetchShareLink]);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      await createShareLink(projectId);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async () => {
    await deleteShareLink(projectId);
    setShowRevokeConfirm(false);
  };

  if (loading) {
    return (
      <div>
        <div className="text-sm text-base-content/80">Loading share link...</div>
      </div>
    );
  }

  return (
    <>
      <div>
        <label className="text-sm text-base-content/80 block mb-1">Share Link</label>
        {shareLink ? (
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono text-base-content block truncate min-w-0 flex-1">
              {shareLink.shareUrl}
            </code>
            <CopyButton text={shareLink.shareUrl} label="Copy" iconOnly className="shrink-0" />
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="btn btn-ghost btn-sm shrink-0"
              title="Generate a new share link (invalidates the current one)"
            >
              {isCreating ? 'Regenerating...' : 'Regenerate'}
            </button>
            <button
              onClick={() => setShowRevokeConfirm(true)}
              className="btn btn-ghost btn-sm text-error shrink-0 ml-2"
            >
              Revoke
            </button>
          </div>
        ) : (
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="btn btn-primary btn-sm"
          >
            {isCreating ? 'Generating...' : 'Generate Share Link'}
          </button>
        )}
      </div>

      <ConfirmDialog
        isOpen={showRevokeConfirm}
        title="Revoke Share Link"
        message="Anyone with this link will no longer be able to view the project. You can generate a new link later."
        confirmText="Revoke"
        variant="danger"
        onConfirm={handleRevoke}
        onCancel={() => setShowRevokeConfirm(false)}
      />
    </>
  );
}
